import { useMemo, useState } from 'react';
import { PAGE_FRAME_MAX, dashboardPageStyle, sectionGridStyle } from '../lib/layout.js';
import { eyebrowStyle, ghostLinkStyle, pageStyle } from '@kilat/ui';
import { Link } from 'react-router-dom';

import { useAuth } from '../lib/use-auth.js';
import { usePlatformBrand } from '../lib/use-platform-brand.js';
import { useTrackedSnapshot } from '../lib/use-tracked-snapshot.js';
import { aiAuditRepo } from '../lib/repositories/ai-audit-repo.js';
import type { AiAuditEvent, AiPersona } from '@kilat/shared';

/**
 * /superadmin/ai — AI observability for the platform owner.
 *
 * Reads `aiAudit/{eventId}` (super-admin-only per Sprint 72 rules) and
 * renders cost / per-persona / per-tenant / cache / error breakdowns.
 *
 * The aiAudit ledger is append-only; one doc per AI call. Sprint 76
 * scope: full-load + in-memory aggregation. Acceptable until daily
 * call volume crosses ~5k (then we add a daily rollover function +
 * read from aggregations).
 *
 * Per [[feedback-no-dead-ends]] + [[feedback-premium-custom-ui]]:
 * every state explicit, custom-crafted UI, never a generic table dump.
 */

const PERSONA_LABELS: Record<AiPersona, string> = {
  ask_kilat_operator: 'Ask Kilat (operator)',
  ask_carwash_customer: 'Ask the carwash (customer)',
  promo_suggest: 'Promo draft',
  broadcast_compose: 'Broadcast compose',
  share_caption: 'Share-kit caption',
  pitch_compose: 'Agent pitch',
  recruiter_insight: 'Recruiter insight',
  super_admin_query: 'Super-admin query',
  internal_automation: 'Internal automation',
};

type RangeKey = 'today' | 'last7' | 'last30' | 'all';
const RANGE_LABELS: Record<RangeKey, string> = {
  today: 'Today',
  last7: 'Last 7 days',
  last30: 'Last 30 days',
  all: 'All time',
};

export function SuperadminAiPage() {
  const { claims, loading: authLoading } = useAuth();
  const brand = usePlatformBrand();
  const [range, setRange] = useState<RangeKey>('today');

  const isSuper = useMemo(() => isSuperAdminForBrand(claims, brand.id), [claims, brand.id]);

  const subscribe = useMemo(() => {
    if (!isSuper) return null;
    return (cb: (vs: AiAuditEvent[]) => void) => aiAuditRepo.listSnapshot(cb);
  }, [isSuper]);

  const {
    data: events,
    loading,
    error,
  } = useTrackedSnapshot<AiAuditEvent[]>(['ai-audit', brand.id], subscribe);

  const filtered = useMemo(() => filterByRange(events ?? [], range), [events, range]);
  const summary = useMemo(() => summarise(filtered), [filtered]);

  if (authLoading) return <Screen title="Loading…" />;
  if (!isSuper) {
    return (
      <Screen
        title="Super-admin only"
        body={`/superadmin/ai is restricted to ${brand.name} super-admins. Sign in with a super-admin account, or head back to the queue.`}
        cta={{ label: 'Back to queue', to: '/queue' }}
      />
    );
  }
  if (error) {
    return (
      <Screen
        title="Couldn't load AI audit log"
        body={error.message}
        cta={{ label: 'Back', to: '/queue' }}
      />
    );
  }

  return (
    <main style={dashboardPageStyle}>
      <Header range={range} onRangeChange={setRange} />
      <section style={contentStyle}>
        {loading || events === null ? (
          <SkeletonGrid />
        ) : filtered.length === 0 ? (
          <EmptyCard range={range} />
        ) : (
          <>
            <KpiRow summary={summary} />
            {/* The two breakdowns share a row on a wide screen; the KPI tiles
                above and the event table below keep the full width. */}
            <div style={sectionGridStyle}>
              <PersonaBreakdown summary={summary} />
              <TenantBreakdown summary={summary} />
            </div>
            <RecentTable events={filtered.slice(0, 50)} />
          </>
        )}
      </section>
    </main>
  );
}

// ── Header ─────────────────────────────────────────────────────────────

function Header({
  range,
  onRangeChange,
}: {
  readonly range: RangeKey;
  readonly onRangeChange: (next: RangeKey) => void;
}) {
  return (
    <header style={headerStyle}>
      <div style={{ maxWidth: PAGE_FRAME_MAX, margin: '0 auto', padding: 'var(--space-5)' }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'baseline',
            gap: 'var(--space-3)',
            flexWrap: 'wrap',
          }}
        >
          <div>
            <p style={eyebrowStyle}>Super-admin · AI observability</p>
            <h1
              style={{
                margin: 'var(--space-1) 0 0 0',
                fontSize: 'var(--text-h1)',
                fontWeight: 600,
              }}
            >
              AI cost &amp; activity
            </h1>
            <p
              style={{
                margin: 'var(--space-2) 0 0 0',
                fontSize: 'var(--text-meta)',
                color: 'var(--color-on-surface-muted)',
              }}
            >
              Every Llama call across every persona. Set <code>LLAMA_API_KEY</code> when the budget
              feels safe.
            </p>
          </div>
          <Link to="/queue" style={ghostLinkStyle}>
            Back to queue
          </Link>
        </div>
        <div
          style={{
            marginTop: 'var(--space-4)',
            display: 'flex',
            gap: 'var(--space-1)',
            background: 'var(--tint-subtle)',
            padding: 4,
            borderRadius: 999,
            width: 'fit-content',
          }}
        >
          {(Object.keys(RANGE_LABELS) as RangeKey[]).map((k) => {
            const active = k === range;
            return (
              <button
                key={k}
                type="button"
                onClick={() => onRangeChange(k)}
                style={{
                  padding: '6px 14px',
                  borderRadius: 999,
                  border: 'none',
                  background: active ? 'var(--color-surface)' : 'transparent',
                  color: active ? 'var(--color-on-surface)' : 'var(--color-on-surface-muted)',
                  fontSize: 'var(--text-meta)',
                  fontWeight: active ? 600 : 500,
                  cursor: 'pointer',
                  boxShadow: active ? '0 1px 2px var(--color-border)' : 'none',
                  fontFamily: 'inherit',
                }}
              >
                {RANGE_LABELS[k]}
              </button>
            );
          })}
        </div>
      </div>
    </header>
  );
}

// ── KPI cards ──────────────────────────────────────────────────────────

function KpiRow({ summary }: { readonly summary: Summary }) {
  const cacheRate =
    summary.totalCalls === 0 ? 0 : Math.round((summary.cacheHits / summary.totalCalls) * 100);
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 180px), 1fr))',
        gap: 'var(--space-3)',
      }}
    >
      <KpiCard label="Total cost" value={formatRm(summary.totalCostMicrocents)} primary />
      <KpiCard label="Calls" value={summary.totalCalls.toLocaleString()} />
      <KpiCard label="Tokens" value={summary.totalTokens.toLocaleString()} />
      <KpiCard label="Cache hit rate" value={`${cacheRate}%`} />
      <KpiCard
        label="Errors"
        value={summary.errors.toString()}
        tone={summary.errors > 0 ? 'warn' : undefined}
      />
    </div>
  );
}

function KpiCard({
  label,
  value,
  primary,
  tone,
}: {
  readonly label: string;
  readonly value: string;
  readonly primary?: boolean;
  readonly tone?: 'warn';
}) {
  const valueColor = primary
    ? 'var(--color-primary)'
    : tone === 'warn'
      ? '#b45309'
      : 'var(--color-on-surface)';
  return (
    <div style={kpiCardStyle}>
      <p
        style={{
          margin: 0,
          fontSize: 11,
          letterSpacing: 0.6,
          textTransform: 'uppercase',
          color: 'var(--color-on-surface-muted)',
          fontWeight: 600,
        }}
      >
        {label}
      </p>
      <p
        style={{
          margin: 'var(--space-2) 0 0 0',
          fontSize: 26,
          fontWeight: 700,
          color: valueColor,
          fontVariantNumeric: 'tabular-nums',
          lineHeight: 1.1,
        }}
      >
        {value}
      </p>
    </div>
  );
}

// ── Per-persona breakdown ──────────────────────────────────────────────

function PersonaBreakdown({ summary }: { readonly summary: Summary }) {
  const rows = Object.entries(summary.byPersona)
    .map(([persona, agg]) => ({ persona: persona as AiPersona, ...agg }))
    .sort((a, b) => b.costMicrocents - a.costMicrocents);
  if (rows.length === 0) return null;
  const maxCost = Math.max(...rows.map((r) => r.costMicrocents), 1);
  return (
    <section style={sectionStyle}>
      <h2 style={sectionTitle}>By persona</h2>
      <div style={{ display: 'grid', gap: 'var(--space-2)' }}>
        {rows.map((r) => (
          <BarRow
            key={r.persona}
            label={PERSONA_LABELS[r.persona]}
            value={formatRm(r.costMicrocents)}
            sub={`${r.calls} calls · ${r.tokens.toLocaleString()} tokens`}
            ratio={r.costMicrocents / maxCost}
          />
        ))}
      </div>
    </section>
  );
}

// ── Per-tenant breakdown ───────────────────────────────────────────────

function TenantBreakdown({ summary }: { readonly summary: Summary }) {
  const rows = Object.entries(summary.byTenant)
    .map(([tenantId, agg]) => ({ tenantId, ...agg }))
    .sort((a, b) => b.costMicrocents - a.costMicrocents)
    .slice(0, 10);
  if (rows.length === 0) return null;
  const maxCost = Math.max(...rows.map((r) => r.costMicrocents), 1);
  return (
    <section style={sectionStyle}>
      <h2 style={sectionTitle}>Top spenders (tenants)</h2>
      <div style={{ display: 'grid', gap: 'var(--space-2)' }}>
        {rows.map((r) => (
          <BarRow
            key={r.tenantId}
            label={
              <code style={{ fontFamily: 'inherit', fontSize: 'var(--text-meta)' }}>
                {r.tenantId === '__unset__' ? '— no tenant —' : r.tenantId}
              </code>
            }
            value={formatRm(r.costMicrocents)}
            sub={`${r.calls} calls`}
            ratio={r.costMicrocents / maxCost}
          />
        ))}
      </div>
    </section>
  );
}

function BarRow({
  label,
  value,
  sub,
  ratio,
}: {
  readonly label: React.ReactNode;
  readonly value: string;
  readonly sub: string;
  readonly ratio: number;
}) {
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'minmax(0, 1fr) auto',
        alignItems: 'center',
        columnGap: 'var(--space-3)',
        rowGap: 4,
      }}
    >
      <div style={{ minWidth: 0 }}>
        <p
          style={{
            margin: 0,
            fontWeight: 500,
            fontSize: 'var(--text-meta)',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {label}
        </p>
        <p style={{ margin: '2px 0 0 0', fontSize: 11, color: 'var(--color-on-surface-muted)' }}>
          {sub}
        </p>
      </div>
      <div style={{ textAlign: 'right' }}>
        <p
          style={{
            margin: 0,
            fontWeight: 600,
            fontVariantNumeric: 'tabular-nums',
            fontSize: 'var(--text-meta)',
          }}
        >
          {value}
        </p>
      </div>
      <div
        style={{
          gridColumn: '1 / span 2',
          height: 4,
          borderRadius: 999,
          background: 'var(--color-border)',
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            width: `${Math.max(2, Math.round(ratio * 100))}%`,
            height: '100%',
            background: 'linear-gradient(90deg, #0d7a4f, #0f8659)',
            transition: 'width 280ms ease',
          }}
        />
      </div>
    </div>
  );
}

// ── Recent calls table ─────────────────────────────────────────────────

function RecentTable({ events }: { readonly events: readonly AiAuditEvent[] }) {
  return (
    <section style={sectionStyle}>
      <h2 style={sectionTitle}>Recent calls</h2>
      <div style={{ display: 'grid', gap: 'var(--space-1)', fontSize: 'var(--text-meta)' }}>
        {events.map((e) => (
          <RecentRow key={e.id} event={e} />
        ))}
      </div>
    </section>
  );
}

function RecentRow({ event }: { readonly event: AiAuditEvent }) {
  const when = event.createdAt?.toMillis ? event.createdAt.toMillis() : 0;
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'auto 1fr auto auto',
        gap: 'var(--space-3)',
        alignItems: 'baseline',
        padding: '8px 12px',
        borderRadius: 8,
        background: 'var(--tint-subtle)',
      }}
    >
      <span
        style={{
          fontVariantNumeric: 'tabular-nums',
          color: 'var(--color-on-surface-muted)',
          fontSize: 11,
        }}
      >
        {formatTime(when)}
      </span>
      <div style={{ minWidth: 0 }}>
        <span style={{ fontWeight: 500 }}>{PERSONA_LABELS[event.persona]}</span>
        {event.errorCode ? (
          <span
            style={{
              marginLeft: 8,
              fontSize: 9,
              padding: '2px 6px',
              borderRadius: 999,
              background: 'rgba(239,68,68,0.10)',
              color: '#991b1b',
              fontWeight: 600,
              letterSpacing: 0.3,
            }}
          >
            {event.errorCode}
          </span>
        ) : null}
        {event.cacheHit ? (
          <span
            style={{
              marginLeft: 8,
              fontSize: 9,
              padding: '2px 6px',
              borderRadius: 999,
              background: 'rgba(13,122,79,0.10)',
              color: 'var(--color-primary)',
              fontWeight: 600,
              letterSpacing: 0.3,
            }}
          >
            CACHED
          </span>
        ) : null}
        {event.provider === 'noop' ? (
          <span
            style={{
              marginLeft: 8,
              fontSize: 9,
              padding: '2px 6px',
              borderRadius: 999,
              background: 'rgba(245,158,11,0.16)',
              color: '#b45309',
              fontWeight: 600,
              letterSpacing: 0.3,
            }}
          >
            STUB
          </span>
        ) : null}
        {event.tenantId ? (
          <span style={{ marginLeft: 8, color: 'var(--color-on-surface-muted)', fontSize: 11 }}>
            · {event.tenantId}
          </span>
        ) : null}
      </div>
      <span
        style={{
          fontVariantNumeric: 'tabular-nums',
          color: 'var(--color-on-surface-muted)',
          fontSize: 11,
        }}
      >
        {event.promptTokens + event.completionTokens}t
      </span>
      <span style={{ fontVariantNumeric: 'tabular-nums', fontWeight: 500 }}>
        {formatRm(event.costMicrocents)}
      </span>
    </div>
  );
}

// ── Empty + screens ────────────────────────────────────────────────────

function EmptyCard({ range }: { readonly range: RangeKey }) {
  return (
    <div style={emptyCardStyle}>
      <p style={{ margin: 0, fontWeight: 600 }}>
        No AI calls in {RANGE_LABELS[range].toLowerCase()}
      </p>
      <p
        style={{
          margin: 'var(--space-2) 0 0 0',
          fontSize: 'var(--text-meta)',
          color: 'var(--color-on-surface-muted)',
          lineHeight: 1.5,
        }}
      >
        Once operators or customers start using Ask Kilat / Ask the carwash / Draft with AI, every
        call lands here with its cost, latency, and cache status. Set <code>LLAMA_API_KEY</code> via{' '}
        <code>firebase functions:secrets:set</code> to switch on real Llama answers — until then
        everything runs in NoOp stub mode (free, zero tokens).
      </p>
    </div>
  );
}

function SkeletonGrid() {
  return (
    <div style={{ display: 'grid', gap: 'var(--space-3)' }}>
      {[0, 1, 2].map((i) => (
        <div
          key={i}
          style={{
            height: 80,
            borderRadius: 12,
            background: 'var(--tint-subtle)',
            animation: 'kilatPulse 1.2s ease-in-out infinite',
          }}
        />
      ))}
      <style>{`@keyframes kilatPulse { 0%, 100% { opacity: 0.5; } 50% { opacity: 1; } }`}</style>
    </div>
  );
}

function Screen({
  title,
  body,
  cta,
}: {
  readonly title: string;
  readonly body?: string;
  readonly cta?: { label: string; to: string };
}) {
  return (
    <main style={{ ...pageStyle, display: 'grid', placeItems: 'center', minHeight: '60vh' }}>
      <div style={{ textAlign: 'center', maxWidth: 480, padding: 'var(--space-6)' }}>
        <h1 style={{ margin: 0, fontSize: 'var(--text-h2)', fontWeight: 600 }}>{title}</h1>
        {body ? (
          <p
            style={{
              margin: 'var(--space-3) 0 0 0',
              color: 'var(--color-on-surface-muted)',
              lineHeight: 1.5,
            }}
          >
            {body}
          </p>
        ) : null}
        {cta ? (
          <Link
            to={cta.to}
            style={{
              display: 'inline-block',
              marginTop: 'var(--space-4)',
              padding: '12px 22px',
              borderRadius: 'var(--radius-md)',
              background: 'var(--color-primary)',
              color: 'var(--color-primary-fg)',
              fontWeight: 500,
              textDecoration: 'none',
            }}
          >
            {cta.label}
          </Link>
        ) : null}
      </div>
    </main>
  );
}

// ── Math + helpers ─────────────────────────────────────────────────────

interface PersonaAgg {
  readonly calls: number;
  readonly tokens: number;
  readonly costMicrocents: number;
}
interface TenantAgg {
  readonly calls: number;
  readonly costMicrocents: number;
}
interface Summary {
  readonly totalCalls: number;
  readonly totalTokens: number;
  readonly totalCostMicrocents: number;
  readonly cacheHits: number;
  readonly errors: number;
  readonly byPersona: Record<string, PersonaAgg>;
  readonly byTenant: Record<string, TenantAgg>;
}

function filterByRange(events: readonly AiAuditEvent[], range: RangeKey): AiAuditEvent[] {
  if (range === 'all') return events.slice();
  const now = Date.now();
  const cutoff =
    range === 'today'
      ? startOfTodayMs(now)
      : range === 'last7'
        ? now - 7 * 24 * 60 * 60 * 1000
        : now - 30 * 24 * 60 * 60 * 1000;
  return events
    .filter((e) => {
      const ms = e.createdAt?.toMillis ? e.createdAt.toMillis() : 0;
      return ms >= cutoff;
    })
    .sort((a, b) => {
      const am = a.createdAt?.toMillis ? a.createdAt.toMillis() : 0;
      const bm = b.createdAt?.toMillis ? b.createdAt.toMillis() : 0;
      return bm - am;
    });
}

function summarise(events: readonly AiAuditEvent[]): Summary {
  const byPersona: Record<string, { calls: number; tokens: number; costMicrocents: number }> = {};
  const byTenant: Record<string, { calls: number; costMicrocents: number }> = {};
  let totalCalls = 0;
  let totalTokens = 0;
  let totalCost = 0;
  let cacheHits = 0;
  let errors = 0;
  for (const e of events) {
    totalCalls++;
    const tokens = (e.promptTokens ?? 0) + (e.completionTokens ?? 0);
    totalTokens += tokens;
    totalCost += e.costMicrocents ?? 0;
    if (e.cacheHit) cacheHits++;
    if (e.errorCode) errors++;
    const pAgg = byPersona[e.persona] ?? { calls: 0, tokens: 0, costMicrocents: 0 };
    byPersona[e.persona] = {
      calls: pAgg.calls + 1,
      tokens: pAgg.tokens + tokens,
      costMicrocents: pAgg.costMicrocents + (e.costMicrocents ?? 0),
    };
    const tenantKey = e.tenantId ?? '__unset__';
    const tAgg = byTenant[tenantKey] ?? { calls: 0, costMicrocents: 0 };
    byTenant[tenantKey] = {
      calls: tAgg.calls + 1,
      costMicrocents: tAgg.costMicrocents + (e.costMicrocents ?? 0),
    };
  }
  return {
    totalCalls,
    totalTokens,
    totalCostMicrocents: totalCost,
    cacheHits,
    errors,
    byPersona,
    byTenant,
  };
}

function startOfTodayMs(nowMs: number): number {
  const d = new Date(nowMs);
  d.setHours(0, 0, 0, 0);
  return d.getTime();
}

function formatRm(microcents: number): string {
  // 1 sen = 100 microcents. 1 RM = 100 sen = 10,000 microcents.
  const rm = microcents / 10_000;
  if (rm < 0.01) return rm === 0 ? 'RM 0' : '< RM 0.01';
  if (rm < 1) return `RM ${rm.toFixed(3)}`;
  if (rm < 100) return `RM ${rm.toFixed(2)}`;
  return `RM ${rm.toFixed(0)}`;
}

function formatTime(ms: number): string {
  if (!ms) return '—';
  const d = new Date(ms);
  const now = new Date();
  const sameDay = d.toDateString() === now.toDateString();
  return sameDay
    ? d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    : `${d.toLocaleDateString([], { month: 'short', day: 'numeric' })} ${d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
}

function isSuperAdminForBrand(claims: Record<string, unknown> | null, brandId: string): boolean {
  if (!claims) return false;
  const sa = (claims as { superadmin?: Record<string, unknown> }).superadmin;
  if (!sa || typeof sa !== 'object') return false;
  return sa[brandId] === true;
}

// ── Styles ─────────────────────────────────────────────────────────────
const headerStyle: React.CSSProperties = {
  background: 'var(--color-surface)',
  borderBottom: '1px solid var(--color-border)',
};
const contentStyle: React.CSSProperties = {
  maxWidth: PAGE_FRAME_MAX,
  margin: '0 auto',
  padding: 'var(--space-5) var(--space-5) var(--space-12) var(--space-5)',
  display: 'grid',
  gap: 'var(--space-5)',
};
const sectionStyle: React.CSSProperties = {
  background: 'var(--color-surface)',
  border: '1px solid var(--color-border)',
  borderRadius: 14,
  padding: 'var(--space-5)',
  boxShadow: '0 1px 2px var(--tint-subtle)',
};
const sectionTitle: React.CSSProperties = {
  margin: '0 0 var(--space-3) 0',
  fontSize: 'var(--text-body)',
  fontWeight: 600,
};
const kpiCardStyle: React.CSSProperties = {
  background: 'var(--color-surface)',
  border: '1px solid var(--color-border)',
  borderRadius: 14,
  padding: 'var(--space-4)',
  boxShadow: '0 1px 2px var(--tint-subtle)',
};
const emptyCardStyle: React.CSSProperties = {
  background: 'var(--color-surface)',
  border: '1px solid var(--color-border)',
  borderRadius: 14,
  padding: 'var(--space-6)',
  textAlign: 'center',
};
